import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on, Action } from '@ngrx/store';

import * as AdministratorActions from './administrator.actions';
import { AdministratorEntity } from './administrator.models';
import { Helper } from '@piqet-angular/data';

export const ADMINISTRATOR_FEATURE_KEY = 'administrator';

export interface AdministratorState extends EntityState<AdministratorEntity> {
  selectedId?: string | number; // which Administrator record has been selected
  loaded: boolean; // has the Administrator list been loaded
  error?: string | null; // last known error (if any)
  usersLoginLogsList: [] | any;
  adminMaterialList: [] | any;
  adminIngredientsList: [] | any;
  loginLogsHistoryData: [] | any;
  adminOrganizationData: [] | any;
  adminOrganizationModaldata: [] | any;
  conversionList: [] | any;
  areaBasedConversionList: [] | any;
  energyandAncillaryList: [] | any;
  transportList: [] | any;
  processingCustomConversionList: [] | any;
  otherDataCircularEconomyParameterList: [] | any;
  processingCustomTruckList: [] | any;
  otherDataScenarioStatusList: [] | any;
  otherDataMaterialList: [] | any;
  refrigeratedTruckList: [] | any;
  storageList: [] | any;
  wasteList: [] | any;
  conversionModaldata: [] | any;
  otherDataProjectStatusList: [] | any;
  adminRecycledList: [] | any;
  eolList: [] | any;
  otherDataPalletTypeList: [] | any;
  otherDataEquivalancyUnitsList: [] | any;
  conversionMappingList: [] | any;
  recycledContent: [] | any;
  otherDataCountryIndetifiersList: [] | any;
  productPackagingRatioList: [] | any;
  miscSimulationList: [] | any;
  logEntriesListData: [] | any;
  countryList: [] | any;
  stateList: [] | any;
  cityList: [] | any;
  importList: [] | any;
  newsList: [] | any;
  rolesList: [] | any;
  stateListById: [] | any;
  adminMaterialModaldata: [] | any;
}

export interface AdministratorPartialState {
  readonly [ADMINISTRATOR_FEATURE_KEY]: AdministratorState;
}

export const administratorAdapter: EntityAdapter<AdministratorEntity> =
  createEntityAdapter<AdministratorEntity>();

export const initialAdministratorState: AdministratorState = administratorAdapter.getInitialState({
  // set initial required properties
  loaded: false,
  usersLoginLogsList: [],
  loginLogsHistoryData: [],
  adminOrganizationData: [],
  adminOrganizationModaldata: [],
  adminRecycledList: [],
  adminMaterialList: [],
  adminIngredientsList: [],
  conversionList: [],
  areaBasedConversionList: [],
  otherDataProjectStatusList: [],
  energyandAncillaryList: [],
  transportList: [],
  processingCustomConversionList: [],
  processingCustomTruckList: [],
  otherDataMaterialList: [],
  refrigeratedTruckList: [],
  otherDataCircularEconomyParameterList: [],
  storageList: [],
  wasteList: [],
  conversionModaldata: [],
  otherDataScenarioStatusList: [],
  eolList: [],
  otherDataPalletTypeList: [],
  otherDataEquivalancyUnitsList: [],
  conversionMappingList: [],
  recycledContent: [],
  otherDataCountryIndetifiersList: [],
  productPackagingRatioList: [],
  miscSimulationList: [],
  logEntriesListData: [],
  countryList: [],
  stateList: [],
  cityList: [],
  importList: [],
  newsList: [],
  rolesList: [],
  stateListById: [],
  adminMaterialModaldata: [],
});

const reducer = createReducer(
  initialAdministratorState,
  on(AdministratorActions.getAllLoginLogs, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getAllLoginLogsSuccess, (state, { usersLoginLogsList }) => {
    return { ...state, usersLoginLogsList: usersLoginLogsList, loaded: true };
  }),
  on(AdministratorActions.getAllLoginLogsFailure, (state, { error }) => ({ ...state, error })),
  // get Login Logs History List
  on(AdministratorActions.openLoginLogsModalSuccess, (state, { loginLogsHistoryData }) => {
    return { ...state, loginLogsHistoryData: loginLogsHistoryData };
  }),
  on(AdministratorActions.getAllLoginLogsHistory, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getAllLoginLogsHistorySuccess, (state, { loginLogsHistoryData }) => {
    return { ...state, loginLogsHistoryData: loginLogsHistoryData, loaded: true };
  }),
  on(AdministratorActions.getAllLoginLogsHistoryFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  // Admin Organization
  on(AdministratorActions.getAdminOrganizationData, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getAdminOrganizationDataSuccess, (state, { adminOrganizationData }) => {
    return { ...state, adminOrganizationData: adminOrganizationData, loaded: true };
  }),
  on(AdministratorActions.getAdminOrganizationDataFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(AdministratorActions.clearAdminOrganization, state => {
    return { ...state, adminOrganizationData: [] };
  }),
  // Admin Organization Modal
  on(
    AdministratorActions.openAddEditAdminOrganizationModalSuccess,
    (state, { adminOrganizationModaldata }) => {
      return { ...state, adminOrganizationModaldata: adminOrganizationModaldata };
    }
  ),

  //Conversion
  on(AdministratorActions.getConversionList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getConversionListSuccess, (state, { conversionList }) => {
    return { ...state, conversionList: conversionList, loaded: true };
  }),
  on(AdministratorActions.getConversionListFailure, (state, { error }) => ({ ...state, error })),

  //Admin recycled
  on(AdministratorActions.getRecycledMaterialList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getRecycledMaterialListSuccess, (state, { recycledMaterialList }) => {
    return { ...state, adminRecycledList: recycledMaterialList, loaded: true };
  }),
  on(AdministratorActions.getRecycledMaterialListFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  // Admin Materials
  on(AdministratorActions.getMaterialList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getMaterialListSuccess, (state, { materialsList }) => {
    return { ...state, adminMaterialList: materialsList, loaded: true };
  }),
  on(AdministratorActions.getMaterialListFailure, (state, { error }) => ({ ...state, error })),
  //Admin ingredients
  on(AdministratorActions.getIngredientsList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getIngredientsListSuccess, (state, { ingredientsList }) => {
    return { ...state, adminIngredientsList: ingredientsList, loaded: true };
  }),
  on(AdministratorActions.getIngredientsListFailure, (state, { error }) => ({ ...state, error })),
  //Area based Conversion
  on(AdministratorActions.getAreaBasedConversionList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(
    AdministratorActions.getAreaBasedConversionListSuccess,
    (state, { areaBasedConversionList }) => {
      return { ...state, areaBasedConversionList: areaBasedConversionList, loaded: true };
    }
  ),
  on(AdministratorActions.getAreaBasedConversionListFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  //Area based Conversion
  on(AdministratorActions.getEnergyandAncillaryList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getEnergyandAncillaryListSuccess, (state, { energyandAncillaryList }) => {
    return { ...state, energyandAncillaryList: energyandAncillaryList, loaded: true };
  }),
  on(AdministratorActions.getEnergyandAncillaryListFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  //Transnport
  on(AdministratorActions.getTransportList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getTransportListSuccess, (state, { transportList }) => {
    return { ...state, transportList: transportList, loaded: true };
  }),
  on(AdministratorActions.getTransportListFailure, (state, { error }) => ({ ...state, error })),
  //processing custom Conversion
  on(AdministratorActions.getProcessingCustomConversionList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(
    AdministratorActions.getProcessingCustomConversionListSuccess,
    (state, { processingCustomConversionList }) => {
      return {
        ...state,
        processingCustomConversionList: processingCustomConversionList,
        loaded: true,
      };
    }
  ),
  on(AdministratorActions.getProcessingCustomConversionListFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  //processing Storage
  on(AdministratorActions.getStorageList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getStorageListSuccess, (state, { storageList }) => {
    return { ...state, storageList: storageList, loaded: true };
  }),
  on(AdministratorActions.getStorageListFailure, (state, { error }) => ({ ...state, error })),
  //waste
  on(AdministratorActions.getWasteList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getWasteListSuccess, (state, { wasteList }) => {
    return { ...state, wasteList: wasteList, loaded: true };
  }),
  on(AdministratorActions.getWasteListFailure, (state, { error }) => ({ ...state, error })),
  // refrigerated truck
  on(AdministratorActions.getRefrigeratedTruckList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getRefrigeratedTruckListSuccess, (state, { refrigeratedTruckList }) => {
    return { ...state, refrigeratedTruckList: refrigeratedTruckList, loaded: true };
  }),
  on(AdministratorActions.getRefrigeratedTruckListFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  // processing custom truck
  on(AdministratorActions.getProcessingCustomTruckList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(
    AdministratorActions.getProcessingCustomTruckListSuccess,
    (state, { processingCustomTruckList }) => {
      return { ...state, processingCustomTruckList: processingCustomTruckList, loaded: true };
    }
  ),
  on(AdministratorActions.getProcessingCustomTruckListFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  //EOL DATA
  on(AdministratorActions.getEolList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getEolListSuccess, (state, { eolList }) => {
    return { ...state, eolList: eolList, loaded: true };
  }),
  on(AdministratorActions.getEolListFailure, (state, { error }) => ({ ...state, error })),
  // other data material
  // For material-types & Recyclability & Compostability & Combustibility
  on(AdministratorActions.getOtherDataMaterialList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getOtherDataMaterialListSuccess, (state, { otherDataMaterialList }) => {
    return { ...state, otherDataMaterialList: otherDataMaterialList, loaded: true };
  }),
  on(AdministratorActions.getOtherDataMaterialListFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  // For CircularEconomyParameter
  on(AdministratorActions.getOtherDataCircularEconomyParameterList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(
    AdministratorActions.getOtherDataCircularEconomyParameterListSuccess,
    (state, { otherDataCircularEconomyParameterList }) => {
      return {
        ...state,
        otherDataCircularEconomyParameterList: otherDataCircularEconomyParameterList,
        loaded: true,
      };
    }
  ),
  on(AdministratorActions.getOtherDataCircularEconomyParameterListFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  // For palletTypePayload
  on(AdministratorActions.getOtherDataPalletTypeList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(
    AdministratorActions.getOtherDataPalletTypeListSuccess,
    (state, { otherDataPalletTypeList }) => {
      return { ...state, otherDataPalletTypeList: otherDataPalletTypeList, loaded: true };
    }
  ),
  on(AdministratorActions.getOtherDataPalletTypeListFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  // For project status
  on(AdministratorActions.getOtherDataProjectstatusList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(
    AdministratorActions.getOtherDataProjectstatusListSuccess,
    (state, { otherDataProjectStatusList }) => {
      return { ...state, otherDataProjectStatusList: otherDataProjectStatusList, loaded: true };
    }
  ),
  on(AdministratorActions.getOtherDataProjectstatusListFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  // For Scenario status
  on(AdministratorActions.getOtherDataScenarioStatusList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(
    AdministratorActions.getOtherDataScenarioStatusListSuccess,
    (state, { otherDataScenarioStatusList }) => {
      return { ...state, otherDataScenarioStatusList: otherDataScenarioStatusList, loaded: true };
    }
  ),
  on(AdministratorActions.getOtherDataScenarioStatusListFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  // For EquivalancyUnits
  on(AdministratorActions.getOtherDataEquivalancyUnitsList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(
    AdministratorActions.getOtherDataEquivalancyUnitsListSuccess,
    (state, { otherDataEquivalancyUnitsList }) => {
      return {
        ...state,
        otherDataEquivalancyUnitsList: otherDataEquivalancyUnitsList,
        loaded: true,
      };
    }
  ),
  on(AdministratorActions.getOtherDataEquivalancyUnitsListFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  // For conversion mapping
  on(AdministratorActions.getConversionMappingList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getConversionMappingListSuccess, (state, { conversionMappingList }) => {
    return { ...state, conversionMappingList: conversionMappingList, loaded: true };
  }),
  on(AdministratorActions.getConversionMappingListFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  // For recycled content
  on(AdministratorActions.getRecycledContentList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getRecycledContentListSuccess, (state, { recycledContentList }) => {
    return { ...state, recycledContent: recycledContentList, loaded: true };
  }),
  on(AdministratorActions.getRecycledContentListFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  // For CountryIndetifiers
  on(AdministratorActions.getOtherDataCountryIndetifiersList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(
    AdministratorActions.getOtherDataCountryIndetifiersListSuccess,
    (state, { otherDataCountryIndetifiersList }) => {
      return {
        ...state,
        otherDataCountryIndetifiersList: otherDataCountryIndetifiersList,
        loaded: true,
      };
    }
  ),
  on(AdministratorActions.getOtherDataCountryIndetifiersListFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  // For Product Packaging Ratio
  on(AdministratorActions.getProductPackageRatioList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(
    AdministratorActions.getProductPackageRatioListSuccess,
    (state, { productPackageRatioList }) => {
      return { ...state, productPackagingRatioList: productPackageRatioList, loaded: true };
    }
  ),
  on(AdministratorActions.getProductPackageRatioListFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  // For MiscSimulation
  on(AdministratorActions.getMiscSimulationList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getMiscSimulationListSuccess, (state, { miscSimulationList }) => {
    return { ...state, miscSimulationList: miscSimulationList, loaded: true };
  }),
  on(AdministratorActions.getMiscSimulationListFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  // For Log Entries

  on(AdministratorActions.getLogEntriesList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getLogEntriesListSuccess, (state, { logEntriesListData }) => {
    return { ...state, logEntriesListData: logEntriesListData, loaded: true };
  }),
  on(AdministratorActions.getLogEntriesListFailure, (state, { error }) => ({ ...state, error })),

  // For Admin Country
  on(AdministratorActions.getAdminCountryList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getAdminCountryListSuccess, (state, { countryList }) => {
    return { ...state, countryList: countryList, loaded: true };
  }),
  on(AdministratorActions.getAdminCountryListFailure, (state, { error }) => ({ ...state, error })),
  // For Admin State
  on(AdministratorActions.getAdminStateList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getAdminStateListSuccess, (state, { stateList }) => {
    return { ...state, stateList: stateList, loaded: true };
  }),
  on(AdministratorActions.getAdminStateListFailure, (state, { error }) => ({ ...state, error })),

  // For Admin City
  on(AdministratorActions.getAdminCityList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getAdminCityListSuccess, (state, { cityList }) => {
    return { ...state, cityList: cityList, loaded: true };
  }),
  on(AdministratorActions.getAdminCityListFailure, (state, { error }) => ({ ...state, error })),

  // For Admin Import
  on(AdministratorActions.getAdminImportList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getAdminImportListSuccess, (state, { importList }) => {
    return { ...state, importList: importList, loaded: true };
  }),
  on(AdministratorActions.getAdminImportListFailure, (state, { error }) => ({ ...state, error })),

  // For Admin News
  on(AdministratorActions.getAdminNewsList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getAdminNewsListSuccess, (state, { newsList }) => {
    return { ...state, newsList: newsList, loaded: true };
  }),
  on(AdministratorActions.getAdminNewsListFailure, (state, { error }) => ({ ...state, error })),

  // For Admin Roles
  on(AdministratorActions.getAdminRolesList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getAdminRolesListSuccess, (state, { rolesList }) => {
    return { ...state, rolesList: rolesList, loaded: true };
  }),
  on(AdministratorActions.getAdminRolesListFailure, (state, { error }) => ({ ...state, error })),

  on(AdministratorActions.getStateForCity, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(AdministratorActions.getStateForCitySuccess, (state, { stateList }) => {
    const updatedStateData = stateList.map((item: any) => {
      return {
        ...item,
        name: Helper.convertToTitleCaseforState(item.name),
      };
    });
    return { ...state, stateListById: updatedStateData, loaded: true };
  }),
  on(AdministratorActions.getStateForCityFailure, (state, { error }) => ({ ...state, error })),

  // Admin material modal success
  on(
    AdministratorActions.openAddEditAdminMaterialsModalSuccess,
    (state, { adminMaterialModaldata }) => {
      return { ...state, adminMaterialModaldata: adminMaterialModaldata };
    }
  )
);

export function administratorReducer(state: AdministratorState | undefined, action: Action) {
  return reducer(state, action);
}
